<script setup lang="ts">
import type {
  DiscountCouponBlock as DiscountCouponBlockType,
  ViewportSize,
} from "@aswin.dev/types";
import { Copy } from "@lucide/vue";
import { computed, inject, onBeforeUnmount, ref, watch } from "vue";
import { EDITOR_KEY } from "../../keys";
import { useI18n } from "../../composables/useI18n";

const props = defineProps<{
  block: DiscountCouponBlockType;
  viewport: ViewportSize;
}>();

const { t } = useI18n();

void props.viewport;

type EditorWithCanvasNav = {
  switchToNextCanvasPage?: () => boolean;
};

const editor = inject(EDITOR_KEY, null) as EditorWithCanvasNav | null;

/** Code shown in the dashed box (sample for auto / empty manual; real code when set). */
const displayedCode = computed(() => {
  const b = props.block;
  if (b.discountMode === "none") {
    return "";
  }
  if (b.discountMode === "manual_shopify") {
    const c = b.manualDiscountCode.trim();
    return c || t.discountCoupon.previewSampleCode;
  }
  return t.discountCoupon.previewSampleCode;
});

/** Message shown in the dashed box during the post-copy flash. */
const confirmationFlashText = computed(() => {
  const raw = props.block.copyConfirmationText?.trim();
  return raw || t.discountCoupon.copyCodeFlash;
});

/** Briefly replaces the code text in the dashed box after a successful copy. */
const showCopiedInBox = ref(false);
let copiedBoxTimer: ReturnType<typeof setTimeout> | null = null;

const COPIED_FLASH_MS = 3000;

function clearCopiedBoxTimer(): void {
  if (copiedBoxTimer) {
    clearTimeout(copiedBoxTimer);
    copiedBoxTimer = null;
  }
}

watch(
  () =>
    [
      props.block.discountMode,
      props.block.manualDiscountCode,
      props.block.valueType,
      props.block.valueAmount,
      props.block.copyConfirmationText,
      props.block.afterCopyAction,
      props.block.afterCopyUrl,
    ] as const,
  () => {
    showCopiedInBox.value = false;
    clearCopiedBoxTimer();
  },
);

function runAfterCopyAction(): void {
  const action = props.block.afterCopyAction ?? "none";
  if (action === "none") return;
  if (action === "next_step") {
    editor?.switchToNextCanvasPage?.();
    return;
  }
  if (action === "load_page") {
    const url = props.block.afterCopyUrl?.trim();
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
    return;
  }
  if (action === "close_popup") {
    return;
  }
}

async function handleCopy(): Promise<void> {
  const code = displayedCode.value;
  if (!code) return;
  try {
    await navigator.clipboard.writeText(code);
    showCopiedInBox.value = true;
    clearCopiedBoxTimer();
    copiedBoxTimer = setTimeout(() => {
      showCopiedInBox.value = false;
      copiedBoxTimer = null;
    }, COPIED_FLASH_MS);
    runAfterCopyAction();
  } catch {
    showCopiedInBox.value = false;
  }
}

onBeforeUnmount(() => {
  clearCopiedBoxTimer();
});
</script>

<template>
  <div
    class="tpl:flex tpl:w-full tpl:flex-col tpl:items-stretch tpl:gap-2 tpl:mx-auto tpl:max-w-xs"
    role="region"
    :aria-label="t.blocks.discountCoupon"
  >
    <div
      class="tpl:flex tpl:items-center tpl:justify-center tpl:border-2 tpl:border-dashed tpl:border-[#a3a3a3] tpl:bg-[var(--tpl-bg)] tpl:px-4 tpl:py-3"
    >
      <template v-if="block.discountMode === 'none'">
        <span
          class="tpl:text-center tpl:text-sm tpl:font-semibold tpl:text-[var(--tpl-text-muted)]"
        >
          {{ t.discountCoupon.previewNoDiscount }}
        </span>
      </template>
      <div
        v-else
        class="tpl:flex tpl:max-w-full tpl:flex-wrap tpl:items-center tpl:justify-center tpl:gap-2"
      >
        <span
          v-if="showCopiedInBox"
          class="tpl:inline-block tpl:max-w-full tpl:text-center tpl:font-sans tpl:text-lg tpl:font-bold tpl:text-[var(--tpl-primary)]"
          role="status"
          aria-live="polite"
        >
          {{ confirmationFlashText }}
        </span>
        <span
          v-else
          class="tpl:inline-block tpl:break-all tpl:text-center tpl:font-sans tpl:text-lg tpl:font-bold tpl:tracking-[0.08em] tpl:text-[var(--tpl-text)]"
          data-tpl-discount-display-code
        >
          {{ displayedCode }}
        </span>
        <button
          type="button"
          class="tpl:flex tpl:size-7 tpl:shrink-0 tpl:cursor-pointer tpl:items-center tpl:justify-center tpl:rounded-md tpl:border-none tpl:bg-transparent tpl:text-[var(--tpl-text-muted)] tpl:outline-none tpl:transition-colors tpl:duration-150 hover:tpl:bg-[var(--tpl-bg-hover)] hover:tpl:text-[var(--tpl-text)] focus-visible:tpl:ring-2 focus-visible:tpl:ring-[var(--tpl-primary)] focus-visible:tpl:ring-offset-2"
          :title="t.discountCoupon.copyCode"
          :aria-label="t.discountCoupon.copyCode"
          @click="handleCopy"
        >
          <Copy :size="14" :stroke-width="2" aria-hidden="true" />
        </button>
      </div>
    </div>
  </div>
</template>
